import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, ShoppingCart } from 'lucide-react';
import { shopApi, useShop, INR } from '../lib/shop';
import ShopNavbar from '../components/ShopNavbar';
import ShopFooter from '../components/ShopFooter';
import PriceDetails from '../components/PriceDetails';
import '../styles/shop.css';

const Step = ({ n, title, children }) => (
  <section className="si-card">
    <p className="px-5 py-3 bg-[var(--si-navy)] text-white rounded-t-md font-bold flex items-center gap-3">
      <span className="w-6 h-6 rounded bg-white text-[var(--si-navy)] text-sm flex items-center justify-center">{n}</span>{title}
    </p>
    <div className="p-5">{children}</div>
  </section>
);

const Field = ({ label, className = '', ...props }) => (
  <label className={`block ${className}`}>
    <span className="block text-sm font-semibold mb-1">{label}</span>
    <input className="si-input" {...props} />
  </label>
);

const Checkout = () => {
  const { cart, refreshCart } = useShop();
  const nav = useNavigate();
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    shipping_address_line1: '', shipping_address_line2: '',
    shipping_city: '', shipping_state: '', shipping_pincode: '', shipping_country: 'IN',
    discount_code: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (u) setForm((f) => ({ ...f, customer_name: u.name || '', customer_email: u.email || '', customer_phone: u.phone || '' }));
  }, []);

  const items = cart.items || [];
  if (items.length === 0) {
    return (
      <div className="si-page"><ShopNavbar />
        <div className="max-w-6xl mx-auto px-3 md:px-6 py-6">
          <div className="si-card py-16 text-center" data-testid="checkout-empty">
            <ShoppingCart className="w-14 h-14 mx-auto text-[var(--si-blue)]" />
            <p className="mt-4 text-xl font-bold">Your cart is empty</p>
            <Link to="/shop" className="mt-5 si-btn si-btn-primary">Continue shopping</Link>
          </div>
        </div>
      <ShopFooter /></div>
    );
  }

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async (e) => {
    e.preventDefault(); setErr(''); setSubmitting(true);
    try {
      const order = await shopApi.createOrder({ ...form, cart_token: localStorage.getItem('cart_token') || '' });
      if (!window.Razorpay) { setErr('Payment library is still loading. Please try again in a moment.'); setSubmitting(false); return; }
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: 'Scholar Industry',
        description: 'Order ' + order.order_id.slice(0, 8),
        image: '/favicon-192x192.png',
        order_id: order.razorpay_order_id,
        prefill: { name: form.customer_name, email: form.customer_email, contact: form.customer_phone },
        theme: { color: '#0B1F4B' },
        handler: async (response) => {
          const verified = await shopApi.verifyOrder({
            order_id: order.order_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          if (verified.ok) {
            localStorage.removeItem('cart_token');
            await refreshCart();
            nav(`/order/${order.order_id}`);
          } else {
            setErr('Payment verification failed. Contact support with payment id ' + response.razorpay_payment_id);
            setSubmitting(false);
          }
        },
        modal: { ondismiss: () => setSubmitting(false) },
      });
      rzp.open();
    } catch (e2) {
      setErr(e2.message || 'Something went wrong');
      setSubmitting(false);
    }
  };

  return (
    <div className="si-page">
      <ShopNavbar />
      <div className="max-w-6xl mx-auto px-3 md:px-6 py-4 md:py-6">
        <form onSubmit={placeOrder} className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-4" data-testid="checkout-form">
          <div className="space-y-4">
            <Step n={1} title="Contact details">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name" required value={form.customer_name} onChange={upd('customer_name')} data-testid="co-name" />
                <Field label="Mobile number" required type="tel" value={form.customer_phone} onChange={upd('customer_phone')} data-testid="co-phone" />
                <Field label="Email" required type="email" value={form.customer_email} onChange={upd('customer_email')} data-testid="co-email" className="sm:col-span-2" />
              </div>
            </Step>
            <Step n={2} title="Delivery address">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="House no., building, street" required value={form.shipping_address_line1} onChange={upd('shipping_address_line1')} data-testid="co-addr1" className="sm:col-span-2" />
                <Field label="Area, landmark (optional)" value={form.shipping_address_line2} onChange={upd('shipping_address_line2')} data-testid="co-addr2" className="sm:col-span-2" />
                <Field label="City" required value={form.shipping_city} onChange={upd('shipping_city')} data-testid="co-city" />
                <Field label="State" required value={form.shipping_state} onChange={upd('shipping_state')} data-testid="co-state" />
                <Field label="PIN code" required inputMode="numeric" pattern="[0-9]{6}" title="6-digit PIN code" value={form.shipping_pincode} onChange={upd('shipping_pincode')} data-testid="co-pincode" />
                <Field label="Country" value={form.shipping_country} onChange={upd('shipping_country')} data-testid="co-country" />
              </div>
            </Step>
            <Step n={3} title="Order summary">
              <div className="divide-y divide-[var(--si-line)]" data-testid="checkout-items">
                {items.map((it) => (
                  <div key={`${it.product_slug}-${it.size}-${it.color}`} className="py-3 flex gap-3">
                    <img src={it.image} alt="" className="w-14 h-[72px] rounded object-cover bg-[var(--si-bg-soft)]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium line-clamp-2">{it.name}</p>
                      <p className="text-sm text-[var(--si-grey)]">{[it.size, it.color].filter(Boolean).join(' · ')} · Qty {it.qty}</p>
                    </div>
                    <p className="text-sm font-bold">{INR(it.line_total)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 max-w-xs">
                <Field label="Discount code (optional)" value={form.discount_code} onChange={upd('discount_code')} data-testid="co-discount" placeholder="Enter code" />
              </div>
            </Step>
            {err && <p className="si-card p-4 text-[#B91C1C] font-semibold border-[#FCA5A5] bg-[#FEF2F2]" data-testid="checkout-error">{err}</p>}
          </div>

          <PriceDetails items={items} subtotal={cart.subtotal} offers={cart.offers || []}>
            <button type="submit" disabled={submitting} className="si-btn si-btn-buy w-full !py-4 text-base" data-testid="checkout-pay-btn">
              <Lock className="w-4 h-4" /> {submitting ? 'Processing…' : 'PAY SECURELY'}
            </button>
            <p className="mt-3 text-xs text-[var(--si-grey)] text-center">Payments are processed securely by Razorpay. Discount codes are applied at payment. All prices include GST. No platform or processing fee is added.</p>
          </PriceDetails>
        </form>
      </div>
      <ShopFooter />
    </div>
  );
};

export default Checkout;
